/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, {forwardRef} from 'react'
import styled from 'styled-components'

import {Box, AtlasCheckbox, AtlasCheckboxProps, SxProp, sx, themeGet, useRefObjectAsForwardedRef} from '../..'

export interface RuleFilterProps extends AtlasCheckboxProps {
  /**
   * Component to render inside the Rule Filter. Used to render a Rule Filter Tag.
   */
  children?: React.ReactNode
  /**
   * Accessible label associated with the checkbox. The label is displayed to the right of the checkbox and of the clickable element to select the checkbox.
   */
  ruleFilterLabel?: string
  /**
   * The value to be used in the checkbox input. This is the value that will be returned on form submission.
   */
  ruleFilterValue?: string
  /**
   * Size of the checkbox label
   */
  labelSize?: 'default' | 'small'
  /**
   * A visual that renders inside the Rule Item after the title and description
   */
  trailingVisual?: string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
  /**
   * Unique identifier for the checkbox
   */
  id?: string
}
const Wrapper = styled(Box)<SxProp>`
  display: grid;
  /* grid-template-areas: 'checkbox' 'visual' 'tag'; */

  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border-radius: ${themeGet('radii.2')};

  padding-left: ${themeGet('space.4')};
  padding-right: ${themeGet('space.4')};
  padding-top: ${themeGet('space.2')};
  padding-top: ${themeGet('space.2')};

  display: grid;
  /* grid-template-areas: 'title action' 'description action'; */
  grid-template-areas: 'checkbox tag';
  grid-template-columns: auto max-content;
  grid-template-rows: ${themeGet('space.4')};
  column-gap: ${themeGet('space.4')};
  align-items: center;
  ${sx}
`
const TagWrapper = styled(Box)<SxProp>`
  grid-area: 'tag';
  grid-row: 1 / 1;
  ${sx}
`
const CheckboxWrapper = styled(Box)<SxProp>`
  grid-area: 'checkbox';
  display: flex;
  grid-row: 1 / 1;
  ${sx}
`
export const RuleFilter = forwardRef(
  (
    {
      children,
      ruleFilterLabel,
      ruleFilterValue,
      trailingVisual: TrailingVisual,
      labelSize = 'default',
      defaultChecked = false,
      id,
      isChecked: isCheckedProp,
      ...props
    }: RuleFilterProps,
    forwardedRef
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)
    const hasTrailingVisual = Boolean(TrailingVisual)

    const [isChecked, setIsChecked] = React.useState(isCheckedProp !== undefined ? isCheckedProp : false)

    const onChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      setIsChecked(current => !current)
    }, [])

    return (
      <Wrapper {...props}>
        <CheckboxWrapper>
          <AtlasCheckbox
            defaultChecked={defaultChecked}
            isChecked={isChecked}
            labelSize={labelSize}
            label={ruleFilterLabel}
            value={ruleFilterValue}
            onChange={onChange}
            id={id}
            ref={innerRef}
          />
          {hasTrailingVisual ? (
            <Box sx={{color: 'icon.subtle'}}>
              {typeof TrailingVisual === 'function' ? <TrailingVisual /> : TrailingVisual}
            </Box>
          ) : null}
        </CheckboxWrapper>
        {React.Children.map(React.Children.toArray(children), (child, idx) => {
          if (!child) {
            return null
          }
          return (
            <TagWrapper>
              {/* eslint-disable-next-line react/no-array-index-key */}
              <React.Fragment key={idx}>
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {isChecked ? React.cloneElement(child as JSX.Element, {isChecked}) : null}
              </React.Fragment>
            </TagWrapper>
          )
        })}
      </Wrapper>
    )
  }
)

RuleFilter.displayName = 'RuleFilter'
